import { IconCheck } from '@tabler/icons-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { TrendConfig } from '@/config/trends';
import { localizeHref } from '@/lib/locale';
import { cn } from '@/lib/utils';

/** "Choose an effect" grid. Each effect is its own page, so tiles are links. */
export function TrendPickerDialog({
  current,
  onOpenChange,
  open,
  trends,
}: {
  current: TrendConfig;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  trends: TrendConfig[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#121212] text-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-sans">Choose an effect</DialogTitle>
          <DialogDescription className="text-white/55">
            Each effect opens its own page. Photos you added here are not
            carried over.
          </DialogDescription>
        </DialogHeader>
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {trends.map((trend) => {
            const isCurrent = trend.id === current.id;
            return (
              <li key={trend.id}>
                <a
                  href={localizeHref(trend.path)}
                  aria-current={isCurrent ? 'page' : undefined}
                  onClick={(event) => {
                    if (!isCurrent) return;
                    event.preventDefault();
                    onOpenChange(false);
                  }}
                  className={cn(
                    'block overflow-hidden rounded-xl border bg-white/[0.03] transition hover:border-[#8ef25a]/60',
                    isCurrent ? 'border-[#8ef25a]' : 'border-white/10'
                  )}
                >
                  <img
                    src={trend.thumbnail}
                    alt=""
                    className="aspect-video w-full object-cover"
                  />
                  <span className="flex items-center justify-between gap-2 px-3 py-2.5 font-medium text-sm">
                    {trend.name}
                    {isCurrent ? (
                      <IconCheck
                        className="size-4 text-[#8ef25a]"
                        aria-label="Current effect"
                      />
                    ) : null}
                  </span>
                </a>
              </li>
            );
          })}
          <li className="flex aspect-video items-center justify-center rounded-xl border border-white/10 border-dashed px-3 text-center text-white/40 text-xs leading-5">
            More effects are on the way
          </li>
        </ul>
      </DialogContent>
    </Dialog>
  );
}
